import {
  CheckRounded,
  CloseFullscreenRounded,
  CloseRounded,
  DescriptionOutlined,
  OpenInFullRounded,
  SearchRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFileDocGroupAndTypesHook } from "../../../hooks/useFileDocGroupAndTypesHook";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { getSuggestedDocTypes, normalizeText } from "./docTypeSuggestions";

// Full-screen-ish document type browser. Replaces the cramped Autocomplete
// dropdown for the (~190 type / ~20 group) doc taxonomy with a searchable,
// group-railed grid. Opened by DocTypePickerField; commits { docGroup, docType }
// on select so it drops in wherever SelectFileAssignmentInput's onChange was.
//
// Currently used ONLY by the document-upload bulk-edit panel — this is a trial
// of the pattern, not a replacement for SelectFileAssignmentInput everywhere.

const ACCENT = "#1F5AF2"; // Lendmarq brand blue
const ACCENT_INK = "#1743C4";
const ACCENT_SOFT = "#EEF2FE";
const CANVAS = "#F3F5F9";
const LINE = "#E2E8F1";
const LINE_SOFT = "#EEF2F7";
const INK = "#0E1626";
const INK_2 = "#3B475C";
const MUTED = "#79879B";
const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

const UNASSIGNED = "__unassigned__";

const sameSelection = (a, b) =>
  !!a && !!b && a.group === b.group && (a.type || "") === (b.type || "");

// Split `text` on the first case-insensitive occurrence of `query` so the match
// can be wrapped in a highlight <mark>-alike.
const HighlightedText = ({ text, query }) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box
        component="span"
        sx={{ background: "#FFF0BE", borderRadius: "2px", px: "1px" }}
      >
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
};

const KeyCap = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: MONO,
      fontSize: 10.5,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderBottomWidth: "2px",
      borderRadius: "4px",
      px: 0.6,
      color: INK_2,
      lineHeight: 1.5,
    }}
  >
    {children}
  </Box>
);

const DocTypePickerModal = ({
  open,
  onClose,
  onChange,
  docGroup,
  docType,
  // Text shown in the header chip — a filename, or "3 selected files".
  subjectLabel,
  // Filenames used to derive the "Suggested" chip row. String or string[].
  suggestFrom,
  // Include the per-group "Unassigned type" catch-all rows (parity with
  // SelectFileAssignmentInput's synthetic entries).
  allowUnassigned = true,
  title = "Assign document type",
}) => {
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();

  const [mode, setMode] = useState("groups"); // "groups" | "alpha"
  // Deliberately NOT reset on open — once you've maximized the picker it stays
  // maximized for the rest of the page session.
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [cursor, setCursor] = useState(-1);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const listRef = useRef(null);
  const railRef = useRef(null);
  const searchRef = useRef(null);
  const sectionRefs = useRef({});
  const optionRefs = useRef({});
  // Suppresses the scroll-spy while a rail click's smooth scroll is in flight,
  // otherwise the intermediate scroll positions yank the active rail item back.
  const railLockRef = useRef(0);

  // ── flat taxonomy ────────────────────────────────────────────────────
  const groups = useMemo(
    () => Object.keys(fileDocGroupAndTypes || {}).sort((a, b) => a.localeCompare(b)),
    [fileDocGroupAndTypes],
  );

  const allItems = useMemo(() => {
    const out = [];
    groups.forEach((group) => {
      (fileDocGroupAndTypes[group] || []).forEach((type) => {
        out.push({ group, type });
      });
    });
    return out;
  }, [groups, fileDocGroupAndTypes]);

  const totalTypes = allItems.length;

  const suggestions = useMemo(
    () => (open ? getSuggestedDocTypes(suggestFrom, allItems) : []),
    [open, suggestFrom, allItems],
  );

  // ── sections (rail + list) ───────────────────────────────────────────
  const sections = useMemo(() => {
    const q = normalizeText(query);
    const matches = (item) =>
      !q ||
      normalizeText(item.type).includes(q) ||
      normalizeText(item.group).includes(q);

    if (mode === "groups") {
      return groups
        .map((group) => {
          const rows = [];
          // Catch-all first, and only when the group itself is in play — a
          // search for "bank" shouldn't surface "Liquidity — unassigned".
          const groupMatched = !q || normalizeText(group).includes(q);
          if (allowUnassigned && groupMatched) {
            rows.push({ group, type: "", isUnassigned: true });
          }
          (fileDocGroupAndTypes[group] || []).forEach((type) => {
            if (matches({ group, type })) rows.push({ group, type });
          });
          return { label: group, rows };
        })
        .filter((s) => s.rows.length)
        .map((s, i) => ({ ...s, id: `doc-type-sec-${i}` }));
    }

    const buckets = new Map();
    [...allItems]
      .filter(matches)
      .sort((a, b) => a.type.localeCompare(b.type))
      .forEach((item) => {
        const letter = (item.type[0] || "#").toUpperCase();
        if (!buckets.has(letter)) buckets.set(letter, []);
        buckets.get(letter).push(item);
      });
    return [...buckets].map(([label, rows], i) => ({
      label,
      rows,
      id: `doc-type-sec-${i}`,
    }));
  }, [mode, query, groups, allItems, fileDocGroupAndTypes, allowUnassigned]);

  const flatRows = useMemo(
    () => sections.flatMap((s) => s.rows),
    [sections],
  );

  // ── open / reset ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setMode("groups");
    setCursor(-1);
    setActiveSectionId(null);
    setSelected(docGroup ? { group: docGroup, type: docType || "" } : null);
    const t = setTimeout(() => {
      searchRef.current?.focus();
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 60);
    return () => clearTimeout(t);
  }, [open, docGroup, docType]);

  useEffect(() => {
    // A new result set means the old cursor index points at a different row.
    setCursor(query ? 0 : -1);
    setActiveSectionId(sections[0]?.id ?? null);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── scroll spy ───────────────────────────────────────────────────────
  const handleListScroll = useCallback(() => {
    if (Date.now() < railLockRef.current) return;
    const list = listRef.current;
    if (!list) return;
    const line = list.scrollTop + 24; // just under the sticky section header
    let current = sections[0]?.id ?? null;
    sections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el && el.offsetTop <= line) current = s.id;
    });
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 4) {
      current = sections[sections.length - 1]?.id ?? current;
    }
    setActiveSectionId(current);
  }, [sections]);

  const jumpToSection = (sectionId) => {
    const el = sectionRefs.current[sectionId];
    const list = listRef.current;
    if (!el || !list) return;
    railLockRef.current = Date.now() + 450;
    list.scrollTo({ top: el.offsetTop - 4, behavior: "smooth" });
    setActiveSectionId(sectionId);
  };

  // ── selection ────────────────────────────────────────────────────────
  const commit = useCallback(
    (row) => {
      if (!row) return;
      onChange?.({ docGroup: row.group, docType: row.type || "" });
      onClose?.();
    },
    [onChange, onClose],
  );

  const moveCursor = useCallback(
    (delta) => {
      if (!flatRows.length) return;
      const next = Math.min(
        flatRows.length - 1,
        Math.max(0, (cursor < 0 ? 0 : cursor) + delta),
      );
      setCursor(next);
      const el = optionRefs.current[next];
      if (el) {
        el.scrollIntoView({ block: "nearest" });
        const section = el.closest("[data-section-id]");
        if (section) setActiveSectionId(section.dataset.sectionId);
      }
    },
    [cursor, flatRows.length],
  );

  // Grid is auto-fill, so the number of columns is only knowable at runtime —
  // read it back off the rendered grid so ↑/↓ move a visual row, not one cell.
  const columnCount = () => {
    const grid = listRef.current?.querySelector("[data-doc-type-grid]");
    if (!grid) return 1;
    const cols = window
      .getComputedStyle(grid)
      .gridTemplateColumns.split(" ")
      .filter(Boolean).length;
    return cols || 1;
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveCursor(columnCount());
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveCursor(-columnCount());
    } else if (e.key === "ArrowRight" && e.target !== searchRef.current) {
      e.preventDefault();
      moveCursor(1);
    } else if (e.key === "ArrowLeft" && e.target !== searchRef.current) {
      e.preventDefault();
      moveCursor(-1);
    } else if (e.key === "Enter") {
      if (cursor >= 0 && flatRows[cursor]) {
        e.preventDefault();
        commit(flatRows[cursor]);
      } else if (selected) {
        e.preventDefault();
        commit(selected);
      }
    } else if (e.key.length === 1 && e.target !== searchRef.current) {
      searchRef.current?.focus();
    }
  };

  const rowKey = (row) => `${row.group}::${row.isUnassigned ? UNASSIGNED : row.type}`;

  let flatIndex = -1; // running index across sections, matched to `flatRows`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          ...(expanded
            ? {
                width: "100vw",
                maxWidth: "100vw",
                height: "100vh",
                maxHeight: "100vh",
                m: 0,
                borderRadius: 0,
              }
            : {
                width: "min(1360px, calc(100% - 24px))",
                maxWidth: "calc(100% - 24px)",
                height: "min(1120px, calc(100vh - 24px))",
                maxHeight: "calc(100vh - 24px)",
                m: 1.5,
                borderRadius: "14px",
              }),
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          color: INK,
        },
      }}
    >
      {/* ── header: title + subject chip + search + suggestions ── */}
      <Box sx={{ p: "16px 18px 0" }}>
        <Flx ac g={1.5} sx={{ mb: 1.6 }}>
          <Txt sx={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {title}
          </Txt>
          {subjectLabel && (
            <Flx
              ac
              g={0.9}
              sx={{
                background: CANVAS,
                border: `1px solid ${LINE_SOFT}`,
                borderRadius: "999px",
                p: "4px 11px 4px 9px",
                minWidth: 0,
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 14, color: MUTED }} />
              <Txt
                sx={{
                  fontSize: 12.5,
                  color: INK_2,
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subjectLabel}
              </Txt>
            </Flx>
          )}
          <Tooltip
            title={expanded ? "Exit full screen" : "Expand to full screen"}
            placement="top"
          >
            <IconButton
              onClick={() => setExpanded((v) => !v)}
              size="small"
              sx={{ ml: "auto", color: MUTED, borderRadius: "7px" }}
              aria-label={expanded ? "Exit full screen" : "Expand to full screen"}
            >
              {expanded ? (
                <CloseFullscreenRounded sx={{ fontSize: 16 }} />
              ) : (
                <OpenInFullRounded sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: MUTED, borderRadius: "7px" }}
            aria-label="Close"
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          {/* zIndex matters: MuiInputBase-root is position:relative and comes
              later in the DOM, so without it the input's background paints
              straight over this icon. */}
          <SearchRounded
            sx={{
              position: "absolute",
              left: 11,
              zIndex: 1,
              pointerEvents: "none",
              fontSize: 17,
              color: MUTED,
            }}
          />
          <InputBase
            inputRef={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search types or groups"
            spellCheck={false}
            autoComplete="off"
            sx={{
              width: "100%",
              border: `1px solid ${LINE}`,
              borderRadius: "9px",
              background: "#FBFCFE",
              p: "9px 100px 9px 36px",
              fontSize: 14.5,
              transition: "border-color 120ms, box-shadow 120ms",
              "&.Mui-focused": {
                borderColor: ACCENT,
                background: "#fff",
                boxShadow: `0 0 0 3px ${ACCENT}1f`,
              },
            }}
          />
          <Flx ac g={1} sx={{ position: "absolute", right: 10 }}>
            {!!query && (
              <IconButton
                size="small"
                onClick={() => {
                  setQuery("");
                  searchRef.current?.focus();
                }}
                sx={{ background: CANVAS, color: MUTED, p: 0.35 }}
                aria-label="Clear search"
              >
                <CloseRounded sx={{ fontSize: 13 }} />
              </IconButton>
            )}
            <Txt
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                color: MUTED,
                background: CANVAS,
                borderRadius: "5px",
                p: "3px 7px",
                whiteSpace: "nowrap",
              }}
            >
              {query
                ? `${flatRows.filter((r) => !r.isUnassigned).length} of ${totalTypes} types`
                : `${totalTypes} types · ${groups.length} groups`}
            </Txt>
          </Flx>
        </Box>

        {!!suggestions.length && (
          <Flx
            ac
            g={1}
            sx={{
              p: "10px 2px",
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            <Txt
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                lineHeight: 1,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: MUTED,
                whiteSpace: "nowrap",
                flex: "none",
                // Optical, not geometric: an all-caps run leaves the descender
                // space empty, so flex-centering its box sits the glyphs ~1px
                // above the chips' mixed-case text.
                position: "relative",
                top: "1px",
              }}
            >
              Suggested
            </Txt>
            {suggestions.map((s) => (
              <Flx
                key={`${s.group}::${s.type}`}
                ac
                g={0.9}
                onClick={() => commit(s)}
                sx={{
                  border: `1px solid ${LINE}`,
                  borderRadius: "999px",
                  p: "5px 12px",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  flex: "none",
                  cursor: "pointer",
                  transition: "all 120ms",
                  "&:hover": {
                    borderColor: ACCENT,
                    background: ACCENT_SOFT,
                    color: ACCENT_INK,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "#0B7C66",
                  }}
                />
                <Txt sx={{ fontSize: 13, color: "inherit" }}>{s.type}</Txt>
                <Txt
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    color: "#0B7C66",
                    background: "#E7F4F1",
                    borderRadius: "4px",
                    p: "1px 5px",
                  }}
                >
                  filename
                </Txt>
              </Flx>
            ))}
          </Flx>
        )}
      </Box>

      {/* ── toolbar: group / alpha toggle ── */}
      <Flx
        ac
        g={1.25}
        sx={{
          borderTop: `1px solid ${LINE_SOFT}`,
          p: "9px 18px",
          background: "#FBFCFE",
          mt: suggestions.length ? 0 : 1.5,
        }}
      >
        <Flx sx={{ background: CANVAS, borderRadius: "7px", p: "2px", gap: "2px" }}>
          {[
            { key: "groups", label: "By group" },
            { key: "alpha", label: "A–Z" },
          ].map((m) => (
            <Box
              key={m.key}
              component="button"
              type="button"
              onClick={() => setMode(m.key)}
              sx={{
                border: 0,
                cursor: "pointer",
                p: "4px 12px",
                borderRadius: "5px",
                fontSize: 12.5,
                fontWeight: 500,
                fontFamily: "inherit",
                background: mode === m.key ? "#fff" : "transparent",
                color: mode === m.key ? INK : MUTED,
                boxShadow:
                  mode === m.key ? "0 1px 2px rgba(14,22,38,.10)" : "none",
              }}
            >
              {m.label}
            </Box>
          ))}
        </Flx>
        <Txt sx={{ ml: "auto", fontFamily: MONO, fontSize: 11.5, color: MUTED }}>
          {query
            ? `${sections.length} ${mode === "groups" ? "group" : "letter"}${
                sections.length === 1 ? "" : "s"
              } matched`
            : mode === "groups"
              ? `All ${groups.length} groups`
              : "Every type, alphabetical"}
        </Txt>
      </Flx>

      {/* ── body: rail + list ── */}
      <Flx sx={{ flex: 1, minHeight: 0, borderTop: `1px solid ${LINE_SOFT}` }}>
        <Box
          ref={railRef}
          sx={{
            width: 242,
            flex: "none",
            borderRight: `1px solid ${LINE_SOFT}`,
            overflowY: "auto",
            p: 1,
          }}
        >
          {sections.map((s) => {
            // Before the first scroll event there's no spied section — the
            // first one is what's under the viewport, so highlight that.
            const active = s.id === (activeSectionId ?? sections[0]?.id);
            return (
              <Flx
                key={s.id}
                ac
                g={1}
                onClick={() => jumpToSection(s.id)}
                sx={{
                  position: "relative",
                  width: "100%",
                  p: "4px 9px",
                  borderRadius: "7px",
                  cursor: "pointer",
                  fontSize: 12.5,
                  color: active ? ACCENT_INK : INK_2,
                  fontWeight: active ? 500 : 400,
                  background: active ? ACCENT_SOFT : "transparent",
                  "&:hover": { background: active ? ACCENT_SOFT : CANVAS },
                  "&::before": active
                    ? {
                        content: '""',
                        position: "absolute",
                        left: -8,
                        top: 3,
                        bottom: 3,
                        width: 2,
                        background: ACCENT,
                        borderRadius: "0 2px 2px 0",
                      }
                    : undefined,
                }}
              >
                <Txt
                  sx={{
                    fontSize: 12.5,
                    color: "inherit",
                    fontWeight: "inherit",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </Txt>
                <Txt
                  sx={{
                    ml: "auto",
                    fontFamily: MONO,
                    fontSize: 11,
                    color: active ? ACCENT_INK : MUTED,
                    flex: "none",
                  }}
                >
                  {s.rows.filter((r) => !r.isUnassigned).length}
                </Txt>
              </Flx>
            );
          })}
        </Box>

        <Box
          ref={listRef}
          onScroll={handleListScroll}
          sx={{
            position: "relative",
            flex: 1,
            overflowY: "auto",
            pb: 3,
            scrollBehavior: "smooth",
          }}
        >
          {!flatRows.length ? (
            <Flx column ac sx={{ p: "56px 20px", textAlign: "center" }}>
              <Txt sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                No document types match “{query}”
              </Txt>
              <Txt sx={{ fontSize: 13, color: MUTED }}>
                Try a shorter term, or search by group — like “title” or
                “insurance”.
              </Txt>
              <Button
                variant="text"
                onClick={() => {
                  setQuery("");
                  searchRef.current?.focus();
                }}
                sx={{ mt: 1, textDecoration: "underline" }}
              >
                Clear search
              </Button>
            </Flx>
          ) : (
            sections.map((section) => (
              <Box
                key={section.id}
                data-section-id={section.id}
                ref={(el) => {
                  sectionRefs.current[section.id] = el;
                }}
              >
                <Flx
                  ac
                  g={1.1}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "#F5F7FC",
                    borderTop: `1px solid ${LINE_SOFT}`,
                    borderBottom: `1px solid ${LINE_SOFT}`,
                    p: "8px 20px",
                    mb: 1.1,
                  }}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 15,
                      borderRadius: "2px",
                      background: ACCENT,
                      opacity: 0.8,
                      flex: "none",
                    }}
                  />
                  <Txt
                    sx={{
                      fontFamily: MONO,
                      fontSize: 14,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: INK,
                    }}
                  >
                    {section.label}
                  </Txt>
                  <Txt
                    sx={{
                      fontFamily: MONO,
                      fontSize: 11,
                      color: MUTED,
                      background: "#fff",
                      border: `1px solid ${LINE}`,
                      borderRadius: "4px",
                      p: "0 5px",
                      lineHeight: "16px",
                    }}
                  >
                    {section.rows.filter((r) => !r.isUnassigned).length}
                  </Txt>
                </Flx>

                <Box
                  data-doc-type-grid
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(270px, 1fr))",
                    gap: "2px 10px",
                    p: "0 14px 16px",
                  }}
                >
                  {section.rows.map((row) => {
                    flatIndex += 1;
                    const i = flatIndex;
                    const isSelected = sameSelection(selected, row);
                    const isCursor = i === cursor;
                    return (
                      // Box, not Flx — Flx doesn't forward refs and the cursor
                      // needs the DOM node to scroll it into view.
                      <Box
                        key={rowKey(row)}
                        ref={(el) => {
                          optionRefs.current[i] = el;
                        }}
                        onMouseMove={() => {
                          if (cursor !== i) setCursor(i);
                        }}
                        onClick={() => {
                          setSelected(row);
                          commit(row);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textAlign: "left",
                          p: "7px 8px",
                          borderRadius: "7px",
                          cursor: "pointer",
                          width: "100%",
                          minWidth: 0,
                          background: isSelected
                            ? ACCENT_SOFT
                            : isCursor
                              ? CANVAS
                              : "transparent",
                          color: isSelected ? ACCENT_INK : isCursor ? INK : INK_2,
                        }}
                      >
                        <Flx column sx={{ minWidth: 0 }}>
                          <Txt
                            sx={{
                              fontSize: 13.5,
                              color: "inherit",
                              fontWeight: isSelected ? 500 : 400,
                              fontStyle: row.isUnassigned ? "italic" : "normal",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.isUnassigned ? (
                              "Unassigned type"
                            ) : (
                              <HighlightedText text={row.type} query={query} />
                            )}
                          </Txt>
                          {mode === "alpha" && (
                            <Txt
                              sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: MUTED,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.group}
                            </Txt>
                          )}
                        </Flx>
                        {isSelected && (
                          <CheckRounded
                            sx={{ ml: "auto", fontSize: 16, color: ACCENT, flex: "none" }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Flx>

      {/* ── footer ── */}
      <Flx
        ac
        g={1.5}
        sx={{
          p: "12px 18px",
          borderTop: `1px solid ${LINE}`,
          background: "#FBFCFE",
        }}
      >
        <Flx ac g={0.75} sx={{ display: { xs: "none", md: "flex" } }}>
          <KeyCap>↑</KeyCap>
          <KeyCap>↓</KeyCap>
          <Txt sx={{ fontSize: 12, color: MUTED }}>move</Txt>
          <KeyCap>↵</KeyCap>
          <Txt sx={{ fontSize: 12, color: MUTED }}>assign</Txt>
          <KeyCap>esc</KeyCap>
          <Txt sx={{ fontSize: 12, color: MUTED }}>close</Txt>
        </Flx>
        <Flx ac g={1} sx={{ ml: "auto", minWidth: 0 }}>
          {selected && (
            <Flx ac g={0.9} sx={{ minWidth: 0 }}>
              <Txt sx={{ fontSize: 12.5, color: MUTED }}>Selected</Txt>
              <Txt
                sx={{
                  fontSize: 12.5,
                  color: INK,
                  fontWeight: 500,
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.type || `${selected.group} — unassigned type`}
              </Txt>
            </Flx>
          )}
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selected}
            onClick={() => commit(selected)}
            sx={{ background: ACCENT, "&:hover": { background: ACCENT_INK } }}
          >
            Assign type
          </Button>
        </Flx>
      </Flx>
    </Dialog>
  );
};

export default DocTypePickerModal;
